import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  HostListener,
  ViewChild,
  ElementRef,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslationService } from '@services/translation.service';
import { FileExplorerDragStateService } from './file-explorer-drag-state.service';

export interface FileFolderMenuItem {
  label: string;
  action?: string; //emits this action if clicked, unless invoke was provided as a function
  invoke?: Function;
  icon?: string;
  menuItems?: FileFolderMenuItem[];
  disabled?: boolean;
  tooltip?: string;
}
export interface FileFolder {
  name: string;
  type: string;
  id: any;
  children?: FileFolder[];
  parentId?: string;
  metadata?: any;
  expanded?: boolean;
  avoidDelete?: boolean;
  avoidEdit?: boolean;
  avoidDrag?: boolean;
  isDraggable?: boolean;
  /** True while a catalog move/reparent request is in flight for this row. */
  moveInProgress?: boolean;
  menuItems?: FileFolderMenuItem[];
  tooltip?: string;
}
export interface MenuState {
  isOpen: boolean;
  top: string;
  left: string;
  item: FileFolder | null;
}

@Component({
  selector: 'app-file-explorer-input',
  templateUrl: './file-explorer-input.component.html',
  styleUrls: ['./file-explorer-input.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileExplorerInputComponent implements OnInit, OnDestroy {
  public filteredItems: FileFolder[] = [];

  @ViewChild('searchInput') searchInput!: ElementRef;
  @Input() canAddFolder: boolean;
  @Input() placeholder = this.translate.getTranslatedContent(
    'search',
    'Search'
  );
  @Input() shouldPersistList: boolean = false;
  @Input() filterBy: string = 'name';
  @Input() backgroundColor: string = 'background';
  @Input() searchTerm: string = '';
  @Input() showList = false;
  @Input() selected = '';
  @Input() items: FileFolder[] = [];
  @Input() resetFilterOnListChange: boolean = false;
  @Input() root = true;
  /** When true, no row is draggable and tree drop targets are inactive. */
  @Input() avoidDrag = false;

  @Output() filterChange = new EventEmitter();
  @Output() focusedEvent = new EventEmitter();
  @Output() toggleMenu = new EventEmitter();
  @Output() itemClicked = new EventEmitter<FileFolder>();
  @Output() searchUpdated = new EventEmitter<string>();
  @Output() searchCleared = new EventEmitter();
  @Output() mouseLeave = new EventEmitter();
  @Output() mouseEnter = new EventEmitter();
  @Output() menuItemClicked = new EventEmitter<{
    item: FileFolder;
    menuItem: FileFolderMenuItem;
  }>();
  @Output() itemDropped = new EventEmitter<{
    item: FileFolder;
    target: FileFolder | null;
  }>();

  public hoveredItem: FileFolder | null = null;
  public draggingItem: FileFolder | null = null;
  @Input() menuState: MenuState = {
    isOpen: false,
    top: '0px',
    left: '0px',
    item: null,
  };
  public submenuState: MenuState = {
    isOpen: false,
    top: '0px',
    left: '0px',
    item: null,
  };
  private documentClickListener;
  public trackBy: (index: number, item: FileFolder) => any;

  constructor(
    private cdr: ChangeDetectorRef,
    private translate: TranslationService,
    public dragState: FileExplorerDragStateService
  ) {}

  @HostListener('mouseleave') onMouseLeave() {
    if (this.root) {
      this.mouseLeave.emit();
    }
  }

  @HostListener('mouseenter') onMouseEnter() {
    if (this.root) {
      this.mouseEnter.emit();
    }
  }

  ngOnInit() {
    // close menu if click occurs outside of menu that is open
    if (this.root) {
      this.documentClickListener = (event: MouseEvent) => {
        if (this.menuState.isOpen) {
          const menu = document.querySelector('.file-explorer-menu');
          const dialog = (event.target as Element)?.closest('scaffold-dialog');
          const clickedInside = menu?.contains(event.target as Node) || dialog;
          if (!clickedInside) {
            this.menuState = {
              isOpen: false,
              top: '0px',
              left: '0px',
              item: null,
            };
            this.closeSubmenu();
            this.cdr.detectChanges();
          }
        }
      };
      document.addEventListener('click', this.documentClickListener);
    }
  }

  ngOnDestroy() {
    if (this.root) {
      document.removeEventListener('click', this.documentClickListener);
    }
  }

  onFolderRowClick(item: FileFolder, _event: MouseEvent): void {
    if (this.dragState.consumePostDropSyntheticRowClick()) {
      return;
    }
    this.onItemClick(item);
  }

  onItemClick(item: FileFolder): void {
    if (this.dragState.consumePostDropSyntheticRowClick()) {
      return;
    }
    if (item.type !== 'folder' && this.root) {
      this.showList = false;
    }

    this.itemClicked.emit(item);
  }

  onSearchInputChange(value: string): void {
    this.filterChange.emit(value);
  }

  clearSearchTerm() {
    this.searchInput.nativeElement.focus();
    this.searchCleared.emit();
  }

  openSubmenu(event: MouseEvent, menuItem: any) {
    if (menuItem?.disabled) return;
    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.submenuState = {
      isOpen: true,
      top: `${rect.top}px`,
      left: `${rect.right}px`,
      item: menuItem,
    };
  }

  handleToggleMenu(event: MouseEvent, item: any) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (item?.moveInProgress) {
      return;
    }

    // need to emit to the root parent to handle the state of it children items
    if (!this.root) {
      return this.toggleMenu.emit({ event, item });
    }

    // if clicking the same item that has an open menu, just close it
    if (this.menuState.item === item && this.menuState.isOpen) {
      this.menuState = {
        isOpen: false,
        top: '0px',
        left: '0px',
        item: null,
      };
      this.closeSubmenu();
      return;
    }

    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.menuState = {
      isOpen: true,
      top: `${rect.bottom + 5}px`,
      left: `${rect.left}px`,
      item: item,
    };
  }

  closeSubmenu() {
    this.submenuState = {
      isOpen: false,
      top: '0px',
      left: '0px',
      item: null,
    };
  }

  handleMenuItemClicked($event, menuItem: FileFolderMenuItem) {
    $event.preventDefault();
    $event.stopPropagation();

    if (menuItem?.disabled) {
      return;
    }

    // call function if provided
    if (menuItem.invoke) {
      menuItem.invoke(menuItem);
    }
    // if provided action, emit it
    if (menuItem.action) {
      this.menuItemClicked.emit({ item: this.menuState.item, menuItem });
    }

    // close menu states
    this.menuState = {
      isOpen: false,
      top: '0px',
      left: '0px',
      item: null,
    };
    this.closeSubmenu();
  }

  focusEvent() {
    this.focusedEvent.emit();
  }

  trackByFn(index: number, item: FileFolder): unknown {
    return this.trackBy ? this.trackBy(index, item) : (item?.id ?? index);
  }

  hasDraggableInTree(nodes: FileFolder[] | undefined): boolean {
    if (this.avoidDrag) return false;
    if (!nodes?.length) return false;
    for (const n of nodes) {
      if (this.itemCanDrag(n)) return true;
      if (n.children?.length && this.hasDraggableInTree(n.children))
        return true;
    }
    return false;
  }

  itemCanDrag(item: FileFolder): boolean {
    if (this.avoidDrag) return false;
    return !!item.isDraggable && !item.avoidDrag && !item.moveInProgress;
  }

  private dndEventTargetElement(event: DragEvent): Element | null {
    const t = event.target;
    if (t instanceof Element) return t;
    if (t && (t as Node).nodeType === Node.TEXT_NODE) {
      return (t as Node).parentElement;
    }
    return null;
  }

  closeOpenMenu(): void {
    this.menuState = {
      isOpen: false,
      top: '0px',
      left: '0px',
      item: null,
    };
    this.closeSubmenu();
    this.cdr.markForCheck();
  }

  onItemDragged(item: FileFolder, event: DragEvent): void {
    if (!this.itemCanDrag(item)) {
      event.preventDefault();
      return;
    }
    event.stopPropagation();
    this.draggingItem = item;
    this.dragState.beginDrag(item);
    event.dataTransfer?.setData('text', JSON.stringify(item));
  }

  onDragEnd(): void {
    this.draggingItem = null;
    this.dragState.endDrag();
  }

  onItemDrop(targetItem: FileFolder, event: DragEvent): void {
    event.preventDefault();
    this.draggingItem = null;
    this.dragState.endDrag();
    if (targetItem?.moveInProgress) {
      event.stopPropagation();
      return;
    }
    const data = event.dataTransfer?.getData('text');
    const draggedItem = data ? JSON.parse(data) : null;
    if (draggedItem && this.itemCanDrag(draggedItem)) {
      this.itemDropped.emit({ item: draggedItem, target: targetItem });
      this.dragState.armPostDropSyntheticRowClick();
      this.suppressSyntheticClickAfterDrop(event.currentTarget as HTMLElement);
    }
    event.stopPropagation();
  }

  onRootDrop(event: DragEvent): void {
    if (!this.hasDraggableInTree(this.items)) return;

    event.preventDefault();
    this.dragState.endDrag();
    const data = event.dataTransfer?.getData('text');
    const draggedItem = data ? JSON.parse(data) : null;
    if (draggedItem && this.itemCanDrag(draggedItem)) {
      this.itemDropped.emit({ item: draggedItem, target: null });
      this.suppressSyntheticClickAfterDrop(event.currentTarget as HTMLElement);
    }
    event.stopPropagation();
  }

  /**
   * After a successful drop, browsers often synthesize pointerdown/mousedown + click on the drop target.
   * Intercept in capture on `document`; only remove listeners after `click` (or timeout) so the click is blocked too.
   */
  private suppressSyntheticClickAfterDrop(host: HTMLElement | null): void {
    if (!host) return;
    let finished = false;
    const cleanup = () => {
      if (finished) return;
      finished = true;
      document.removeEventListener('pointerdown', block, true);
      document.removeEventListener('mousedown', block, true);
      document.removeEventListener('click', block, true);
      clearTimeout(timeoutId);
    };
    const block = (e: Event) => {
      const t = e.target;
      if (!t || !host.contains(t as Node)) return;
      e.preventDefault();
      (e as MouseEvent).stopImmediatePropagation?.();
      e.stopPropagation();
      if (e.type === 'click') {
        cleanup();
      }
    };
    document.addEventListener('pointerdown', block, true);
    document.addEventListener('mousedown', block, true);
    document.addEventListener('click', block, true);
    const timeoutId = window.setTimeout(cleanup, 600);
  }

  onRootDragOver(event: DragEvent): void {
    if (!this.hasDraggableInTree(this.items)) return;

    event.preventDefault();
    event.stopPropagation();
  }

  onRootDropZoneDragEnter(event: DragEvent): void {
    if (!this.root || !this.hasDraggableInTree(this.items)) return;
    if (!this.dragState.isDragging()) return;
    const rootEl = event.currentTarget as HTMLElement;
    const t = this.dndEventTargetElement(event);
    const rowLi = t?.closest?.('li');
    // Any list row (including nested) — do not show "drop on root" chrome.
    if (rowLi && rootEl.contains(rowLi)) {
      return;
    }
    event.preventDefault();
    this.dragState.setRootDropHover(true);
  }

  onRootDropZoneDragLeave(event: DragEvent): void {
    if (!this.root) return;
    const cur = event.currentTarget as HTMLElement;
    const rel = event.relatedTarget as Node | null;
    if (rel && cur.contains(rel)) return;
    this.dragState.setRootDropHover(false);
  }

  onRowDragEnter(item: FileFolder, event: DragEvent): void {
    if (!this.dragState.isDragging()) return;
    const currentLi = event.currentTarget as HTMLElement;
    const innermostLi = this.dndEventTargetElement(event)?.closest?.('li');
    if (innermostLi && innermostLi !== currentLi) {
      return;
    }
    event.preventDefault();
    const src = this.dragState.getDragSource();
    if (!src) return;
    if (item.type === 'folder') {
      if (item.moveInProgress) {
        this.dragState.setFolderHover(null);
        return;
      }
      if (item.id === src.id) {
        this.dragState.setFolderHover(null);
        return;
      }
      this.dragState.setFolderHover(item.id);
    } else {
      this.dragState.setFolderHover(null);
    }
  }

  onRowDragLeave(item: FileFolder, event: DragEvent): void {
    if (!this.dragState.isDragging()) return;
    const cur = event.currentTarget as HTMLElement;
    const rel = event.relatedTarget as Node | null;
    if (rel && cur.contains(rel)) return;
    if (item.type === 'folder') {
      this.dragState.clearFolderHoverIfMatches(item.id);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  handleItemDropped(event: {
    item: FileFolder;
    target: FileFolder | null;
  }): void {
    this.itemDropped.emit(event);
  }
}
